import { useCallback, useEffect, useRef } from 'react';
import React from 'react';
import {
  GridApi,
  GridOptions,
  IRowNode,
  RowDragEndEvent,
  RowDragMoveEvent,
  RowDropZoneParams,
} from 'ag-grid-community';
import { isTruthy } from '@ardoq/common-helpers';
import { invariant } from '@ardoq/invariant';
import {
  ExternalDropZonesProps,
  GroupingColumn,
  Row,
  UseControlledAGGridProps,
} from '../types';
import { getRowDragText } from '../utils';

/**
 * Gets the rows to include in a drop operation.
 * If the dragged row is selected, includes all selected rows.
 * Otherwise, includes only the dragged row.
 */
const getDroppedRows = <RowData, RowId extends string = string>(
  api: GridApi<Row<RowData, RowId>>,
  draggedRowData: Row<RowData, RowId>
): Row<RowData, RowId>[] => {
  const selectedRows = api.getSelectedRows();
  const isDraggedRowSelected = selectedRows.some(
    row => row?.id === draggedRowData.id
  );

  return isDraggedRowSelected
    ? selectedRows.filter(isTruthy)
    : [draggedRowData];
};

/**
 * Manages drop zone registration with AG Grid.
 * Re-registers when dropZoneIds change and cleans up on unmount.
 * Returns a register function for manual registration (e.g., onGridReady).
 */
const useRegisterDropZones = <RowData, RowId extends string = string>(
  apiRef: React.RefObject<GridApi<Row<RowData, RowId>> | null>,
  propsRef: React.RefObject<ExternalDropZonesProps<RowData, RowId> | undefined>,
  dropZoneIdsKey: string,
  enabled: boolean
): ((api: GridApi<Row<RowData, RowId>>) => void) => {
  const registeredZonesRef = useRef<RowDropZoneParams[]>([]);

  const register = (api: GridApi<Row<RowData, RowId>>) => {
    const currentProps = propsRef.current;
    if (!currentProps) {
      return;
    }

    // Clean up previously registered drop zones. This handles the case when
    // dropZoneIds changes while still passing the prop. The zones from
    // onGridReady were never tracked by useEffect (it returned early on
    // initial mount because apiRef was null), so this cleanup removes them.
    for (const params of registeredZonesRef.current) {
      api.removeRowDropZone(params);
    }
    registeredZonesRef.current = [];

    for (const dropZoneId of currentProps.dropZoneIds) {
      const params: RowDropZoneParams = {
        getContainer: () => {
          const dropZone = document.getElementById(dropZoneId);

          invariant(
            dropZone,
            `Drop zone element with ID "${dropZoneId}" not found.`
          );

          return dropZone;
        },
        onDragEnter: () => propsRef.current?.onDragEnterDropZone?.(dropZoneId),
        onDragLeave: () => propsRef.current?.onDragLeaveDropZone?.(dropZoneId),
        onDragStop: params => {
          const currentProps = propsRef.current;
          const draggedRowData = params.node.data;

          if (!currentProps || !draggedRowData) {
            return;
          }

          const droppedRows = getDroppedRows(api, draggedRowData);
          currentProps.onExternalDrop(dropZoneId, droppedRows);
        },
      };

      api.addRowDropZone(params);
      registeredZonesRef.current.push(params);
    }
  };

  useEffect(() => {
    const api = apiRef.current;
    if (!api || !enabled) {
      return;
    }

    register(api);

    // Cleanup when the props for externalDrop is removed entirely. The register
    // function has its own cleanup, but that only runs when register is called.
    // This cleanup handles the case when enabled becomes false (prop removed),
    // since we don't call register in that case.
    return () => {
      if (api.isDestroyed()) {
        registeredZonesRef.current = [];
        return;
      }
      for (const params of registeredZonesRef.current) {
        api.removeRowDropZone(params);
      }
      registeredZonesRef.current = [];
    };
    // register reads from refs only, so it's safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropZoneIdsKey, enabled]);

  return register;
};

/**
 * Sets aria-expanded attribute on row DOM elements for rows that can accept drops.
 * This is necessary because AG Grid doesn't automatically set aria-expanded for
 * empty folders (nodes with allowDrop but no children).
 */
export const useRowAriaExpanded = <RowId extends string = string>(
  api: GridApi | null,
  expandedIds: RowId[] | undefined
) => {
  const expandedIdsRef = useRef(expandedIds);
  useEffect(() => {
    expandedIdsRef.current = expandedIds;
  }, [expandedIds]);

  // Listen to modelUpdated to re-apply attributes after AG Grid re-renders rows
  useEffect(() => {
    if (!api) {
      return;
    }

    const onModelUpdated = () => {
      requestAnimationFrame(() => {
        if (api.isDestroyed()) {
          return;
        }
        const expandedSet = new Set(expandedIdsRef.current ?? []);

        api.forEachNode(node => {
          const allowDrop = node.data?.allowDrop ?? false;
          const nodeId = node.id;

          if (!nodeId) {
            return;
          }

          // Find the row DOM element using AG Grid's row-id attribute
          const rowElement = document.querySelector<HTMLElement>(
            `[row-id="${nodeId}"]`
          );

          if (!rowElement) {
            return;
          }

          if (allowDrop) {
            const isExpanded = expandedSet.has(nodeId as RowId);
            rowElement.setAttribute('aria-expanded', String(isExpanded));
          }
        });
      });
    };

    api.addEventListener('modelUpdated', onModelUpdated);

    return () => {
      if (api.isDestroyed()) {
        return;
      }
      api.removeEventListener('modelUpdated', onModelUpdated);
    };
  }, [api]);
};

/**
 * Determines if a folder node is ready to accept drops.
 * With dropIntoClosedFolders, every folder is ready. Otherwise, a folder is
 * ready if:
 * 1. It's currently expanded (node.expanded = true), OR
 * 2. It was expanded during this drag operation (in expandedFolderIds Set), OR
 * 3. It's in the current expanded state (in currentExpandedIds list)
 *
 * Note: For empty folders, AG Grid never sets node.expanded to true,
 * so we must rely on currentExpandedIds from the application state.
 */
const isFolderReadyForDrop = <RowData, RowId extends string = string>(
  node: IRowNode<Row<RowData, RowId>> | null | undefined,
  currentExpandedIds: RowId[] | undefined,
  dropIntoClosedFolders: boolean | undefined
): boolean => {
  if (!node?.data?.allowDrop) {
    return false;
  }

  if (dropIntoClosedFolders) {
    return true;
  }

  const nodeId = node.id;

  return (
    node.expanded ||
    (nodeId !== null &&
      nodeId !== undefined &&
      currentExpandedIds?.includes(nodeId as RowId)) ||
    false
  );
};

const OPEN_FOLDER_TIMEOUT = 200;

/**
 * When dragging a row, if the row is over a folder, we want to expand the folder
 * after a short delay. The delay is needed to prevent accidental expansions when
 * dragging over a folder.
 *
 * @param getCurrentExpandedIds - A function that returns the current list of expanded folder IDs.
 * Using a function ensures we always get the latest value, even if it changes during a drag operation.
 * This is used to identify folders that are already expanded (especially empty folders,
 * which AG Grid doesn't mark as expanded).
 */
const getOnRowDragMove = <RowData, RowId extends string = string>(
  expandedIdsRef: React.RefObject<RowId[] | undefined>,
  dragExpandedEmptyFoldersRef: React.RefObject<Set<RowId>>,
  groupingColumnRef: React.RefObject<
    GroupingColumn<RowData, RowId> | undefined
  >,
  internalDropPropsRef: React.RefObject<
    { dropIntoClosedFolders?: boolean } | undefined
  >
) => {
  let expandTimeout: NodeJS.Timeout | null = null;

  const onMove = (event: RowDragMoveEvent<Row<RowData, RowId>>) => {
    const overNode = event.overNode;
    if (!overNode) {
      return;
    }

    // Determine if the hovered node is a folder
    const hoveredFolder = overNode.data?.allowDrop ? overNode : null;
    const currentExpandedIds = expandedIdsRef.current;
    const folderReady = isFolderReadyForDrop(
      hoveredFolder,
      currentExpandedIds,
      internalDropPropsRef.current?.dropIntoClosedFolders
    );

    // Only use the hovered folder as drop target if it's ready to accept drops.
    // Otherwise, use the parent. This prevents dropping into folders before they're opened.
    const dropTarget = folderReady ? hoveredFolder : overNode.parent;

    event.api.setRowDropPositionIndicator({
      row: dropTarget,
      dropIndicatorPosition: 'inside',
    });

    // Collapse empty folders we expanded during this drag that are no longer hovered
    const emptyFolders = dragExpandedEmptyFoldersRef.current;
    for (const folderId of emptyFolders) {
      if (folderId !== hoveredFolder?.id) {
        const node = event.api.getRowNode(folderId);
        if (node) {
          event.api.setRowNodeExpanded(node, false);
        }
        groupingColumnRef.current?.onRowExpansionToggle(folderId, false);
        emptyFolders.delete(folderId);
      }
    }

    if (expandTimeout) {
      clearTimeout(expandTimeout);
      expandTimeout = null;
    }

    // Start the timer for any collapsed folder that we haven't already expanded
    // and that isn't currently expanded (per application state)
    const isCurrentlyExpanded =
      hoveredFolder?.id &&
      currentExpandedIds?.includes(hoveredFolder.id as RowId);

    if (
      hoveredFolder &&
      hoveredFolder.id &&
      !hoveredFolder.expanded &&
      !isCurrentlyExpanded
    ) {
      const folderId = hoveredFolder.id;
      expandTimeout = setTimeout(() => {
        event.api.setRowNodeExpanded(hoveredFolder, true);
        expandTimeout = null;

        if (!hoveredFolder.childrenAfterGroup?.length) {
          dragExpandedEmptyFoldersRef.current.add(folderId as RowId);
        }

        // Update the drop indicator now that the folder is ready
        // Check if we're still hovering over the same folder
        if (overNode.id === folderId) {
          const isNowReady = isFolderReadyForDrop(
            hoveredFolder,
            expandedIdsRef.current,
            internalDropPropsRef.current?.dropIntoClosedFolders
          );
          const newDropTarget = isNowReady ? hoveredFolder : overNode.parent;
          event.api.setRowDropPositionIndicator({
            row: newDropTarget,
            dropIndicatorPosition: 'inside',
          });
        }
      }, OPEN_FOLDER_TIMEOUT);
    }
  };

  const cleanup = (api: GridApi) => {
    if (expandTimeout) {
      // Make sure that we don't open the folder after the drag + hover has ended
      clearTimeout(expandTimeout);
      expandTimeout = null;
    }

    const emptyFolders = dragExpandedEmptyFoldersRef.current;
    for (const folderId of emptyFolders) {
      const node = api.getRowNode(folderId);
      if (node) {
        api.setRowNodeExpanded(node, false);
      }
      groupingColumnRef.current?.onRowExpansionToggle(folderId as RowId, false);
    }
    emptyFolders.clear();
  };

  return { onMove, cleanup };
};

/**
 * Checks if the target node is a descendant of the source node.
 * This prevents dropping a parent folder into its own child.
 */
const isDescendantOf = <RowData, RowId extends string = string>(
  api: GridApi<Row<RowData, RowId>>,
  targetNodeId: string | null,
  sourceNodeId: string
): boolean => {
  if (!targetNodeId) {
    return false;
  }

  const targetNode = api.getRowNode(targetNodeId);
  if (!targetNode) {
    return false;
  }

  let currentNode = targetNode.parent;
  while (currentNode) {
    if (currentNode.id === sourceNodeId) {
      return true;
    }
    currentNode = currentNode.parent;
  }

  return false;
};

/**
 * Combined hook for all drag and drop functionality (internal and external).
 * This ensures event handlers don't override each other when both are enabled.
 */
export const useDragAndDrop = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  groupingColumn: GroupingColumn<RowData, RowId> | undefined
) => {
  const internalDropProps = props.dragAndDroppable?.internalDrop;
  const externalDropProps = props.dragAndDroppable?.externalDrop;
  const internalDropEnabled = internalDropProps !== undefined;
  const externalDropZoneEnabled = externalDropProps !== undefined;
  const dragAndDropEnabled = internalDropEnabled || externalDropZoneEnabled;

  const expandedIds = groupingColumn?.expandedIds;

  // Refs for accessing latest values in callbacks without recreating handlers
  const expandedIdsRef = useRef(expandedIds);
  useEffect(() => {
    expandedIdsRef.current = expandedIds;
  }, [expandedIds]);

  const groupingColumnRef = useRef(groupingColumn);
  groupingColumnRef.current = groupingColumn;

  const dragExpandedEmptyFoldersRef = useRef(new Set<RowId>());

  const internalDropPropsRef = useRef(internalDropProps);
  internalDropPropsRef.current = internalDropProps;

  const externalDropPropsRef = useRef(externalDropProps);
  externalDropPropsRef.current = externalDropProps;

  // Read through a ref at drag time — gridOptions callbacks are captured once
  // at grid creation.
  const rowDragTextFn =
    props.reorderable?.rowDragText ?? props.dragAndDroppable?.rowDragText;
  const rowDragTextFnRef = useRef(rowDragTextFn);
  rowDragTextFnRef.current = rowDragTextFn;

  // Set from onGridReady (via handleInitialDropZones), so it is populated
  // whether or not external drop zones are enabled.
  const externalApiRef = useRef<GridApi<Row<RowData, RowId>> | null>(null);
  const hasFiredDragStartRef = useRef(false);
  const mouseUpHandlerRef = useRef<(() => void) | null>(null);
  const dropZoneIdsKey = externalDropProps?.dropZoneIds.join(',') ?? '';

  // Cleanup mouseup listener on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (mouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', mouseUpHandlerRef.current);
        mouseUpHandlerRef.current = null;
      }
    };
  }, []);

  // Register external drop zones with AG Grid
  const registerInitialDropZones = useRegisterDropZones<RowData, RowId>(
    externalApiRef,
    externalDropPropsRef,
    dropZoneIdsKey,
    externalDropZoneEnabled
  );

  // Create drag move handlers for internal drop (folder hover animation)
  const dragMoveHandlers = getOnRowDragMove<RowData, RowId>(
    expandedIdsRef,
    dragExpandedEmptyFoldersRef,
    groupingColumnRef,
    internalDropPropsRef
  );

  // Combined onRowDragEnd handler
  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<Row<RowData, RowId>>) => {
      // Handle external drop drag end (reset drag start flag)
      if (hasFiredDragStartRef.current) {
        hasFiredDragStartRef.current = false;
        externalDropPropsRef.current?.onDragEnd?.();
      }

      // Handle internal drop
      const sourceRowId = event.node.id as RowId | undefined;
      const currentInternalDropConfig = internalDropPropsRef.current;
      if (!currentInternalDropConfig || !sourceRowId) {
        return;
      }

      // Check if the folder is ready to accept drops
      const folderReady = isFolderReadyForDrop(
        event.overNode,
        expandedIdsRef.current,
        currentInternalDropConfig.dropIntoClosedFolders
      );

      // Only drop into a folder if it's ready. Otherwise, drop into its parent.
      const targetId = folderReady
        ? (event.overNode?.data?.id ?? null)
        : (event.overNode?.parent?.data?.id ?? null);

      // Prevent dropping an item into itself
      if (sourceRowId === targetId) {
        return;
      }

      // Prevent dropping a parent into its own descendant
      if (isDescendantOf(event.api, targetId, sourceRowId)) {
        return;
      }

      currentInternalDropConfig.onDrop(sourceRowId, targetId as RowId | null);
    },
    []
  );

  // Combined onRowDragMove handler
  const onRowDragMove = useCallback(
    (event: RowDragMoveEvent<Row<RowData, RowId>>) => {
      // Handle external drop drag start notification
      if (externalDropPropsRef.current && !hasFiredDragStartRef.current) {
        hasFiredDragStartRef.current = true;

        // Get the rows being dragged
        const draggedRowData = event.node.data;
        const draggedRows = draggedRowData
          ? getDroppedRows(event.api, draggedRowData)
          : [];

        externalDropPropsRef.current.onDragStart?.(draggedRows);
        // Catch drag end outside of AG Grid or drop zones
        const handleMouseUp = () => {
          if (hasFiredDragStartRef.current) {
            hasFiredDragStartRef.current = false;
            externalDropPropsRef.current?.onDragEnd?.();
          }
          mouseUpHandlerRef.current = null;
        };
        mouseUpHandlerRef.current = handleMouseUp;
        document.addEventListener('mouseup', handleMouseUp, { once: true });
      }

      // Handle internal drop folder hover animation
      if (internalDropPropsRef.current) {
        dragMoveHandlers.onMove(event);
      }
    },
    [dragMoveHandlers]
  );

  // Called once from onGridReady
  const handleInitialDropZones = (api: GridApi<Row<RowData, RowId>>) => {
    externalApiRef.current = api;
    registerInitialDropZones(api);
  };

  const clearDropIndicator = (api: GridApi) => {
    dragMoveHandlers.cleanup(api);
    api.setRowDropPositionIndicator({
      row: null,
      dropIndicatorPosition: 'none',
    });
  };

  const dragAndDroppableProps: GridOptions<Row<RowData, RowId>> | undefined =
    dragAndDropEnabled
      ? {
          onRowDragEnd,
          onDragStopped: event => clearDropIndicator(event.api),
          onRowDragMove,
          onRowDragLeave: event => clearDropIndicator(event.api),
          onRowDragCancel: event => clearDropIndicator(event.api),
          rowDragEntireRow: Boolean(props.dragAndDroppable?.dragEntireRow),
          // The grouping column's rowDragText only covers drags started from
          // the drag handle; this grid-level one covers entire-row drags.
          rowDragText: (params, dragItemCount) => {
            const currentRowDragTextFn = rowDragTextFnRef.current;
            if (!currentRowDragTextFn) {
              return params.defaultTextValue ?? `${dragItemCount}`;
            }
            return getRowDragText(
              params,
              externalApiRef.current,
              currentRowDragTextFn
            );
          },
        }
      : undefined;

  return {
    externalDropZoneEnabled,
    handleInitialDropZones,
    dragAndDroppableProps,
  };
};
